"use client";

import { useState } from "react";
import {
  Checkbox,
  ErrorNotice,
  Notice,
  SelectField,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2 } from "@/components/tool-kit/styles";
import {
  ConcentrationView,
  type Firm,
  MergerView,
} from "@/components/tools/econ-micro/concentration-view";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseCell, parseLabeledRows } from "@/lib/tool-kit/charts/parse";
import { merger, toShares } from "@/lib/tool-kit/econ-micro/concentration";

/** English strings. A translated content file overrides these through `ui`. */
export const HERFINDAHL_INDEX_CALCULATOR_STRINGS = {
  dataLabel: "Firms and their shares — one per line: name, number",
  dataHint:
    "Separate the name from the number with a comma, tab or semicolon (use semicolons if your numbers have decimal commas). A line with only a number is a firm with that number. A first line without numbers is read as a header.",
  exampleData:
    "Firm A, 30\nFirm B, 25\nFirm C, 20\nFirm D, 10\nFirm E, 10\nFirm F, 5",
  kindLabel: "The numbers are",
  kindShares: "Market shares (percent)",
  kindSales: "Sales or revenue (converted to shares)",
  rescale: "Scale the shares so they add up to 100%",
  firmDefault: "Firm {n}",
  results: "Results",
  noFirms: "Enter at least one firm with a number above 0.",
  negative: "Shares and sales cannot be negative.",
  skippedLines:
    "one:Line {lines} has no number and was left out.|other:Lines {lines} have no numbers and were left out.",
  sumNotHundred:
    "The shares add up to {total}%, not 100%. The index counts only the firms listed, so it is a lower bound if others are missing. Tick the box to scale them to 100%.",
  statHhi: "Herfindahl–Hirschman Index",
  statBand: "Concentration (2023 Merger Guidelines)",
  bandHighly: "Highly concentrated",
  bandNotHighly: "Not highly concentrated",
  statEquivalent: "Same as this many equal-sized firms",
  statCr1: "Largest firm (CR1)",
  statCr4: "Four largest firms (CR4)",
  statFirms: "Firms counted",
  bandNote:
    "The 2023 DOJ and FTC Merger Guidelines call a market highly concentrated when its HHI is above 1,800. The 2010 Horizontal Merger Guidelines used 1,500 and 2,500 as their bands.",
  tableHeading: "Where the index comes from",
  columnFirm: "Firm",
  columnShare: "Share (%)",
  columnSquare: "Share squared",
  columnPart: "Part of the HHI",
  workSum: "HHI = the sum of the squared shares = {index}",
  mergerHeading: "Test a merger",
  mergerHint:
    "Choose two firms to combine. The change in the index is 2 × the product of their shares.",
  firstFirm: "First firm",
  secondFirm: "Second firm",
  sameFirm: "Choose two different firms.",
  mergerResultHeading: "Merger result",
  workIncrease: "Change in HHI = 2 × {a} × {b} = {delta}",
  workAfter: "HHI after the merger = {before} + {delta} = {after}",
  workMergedShare: "Merged firm’s share = {a} + {b} = {share}%",
  presumedYes:
    "Under Guideline 1 of the 2023 Merger Guidelines, this merger is presumed to substantially lessen competition.",
  reasonMarket:
    "The market would be highly concentrated (HHI of {after}, above 1,800) and the HHI would rise by {delta}, more than 100.",
  reasonShare:
    "The merged firm would hold {share}% of the market, above 30%, and the HHI would rise by {delta}, more than 100.",
  presumedNo:
    "The structural presumption is not triggered. It needs an HHI increase of more than 100 and either a post-merger HHI above 1,800 or a merged share above 30%. Here the increase is {delta}, the HHI after is {after} and the merged share is {share}%.",
  mergerCaveat:
    "A presumption is where the agencies start, not a finding. It can be rebutted, and a merger below these thresholds can still be challenged. This is an educational screen, not legal advice.",
} as const;

export type HerfindahlIndexStrings = typeof HERFINDAHL_INDEX_CALCULATOR_STRINGS;
type Kind = "shares" | "sales";

/** Firms from the pasted text; a line of bare numbers is a list of firms. */
function readFirms(raw: string, fallbackName: (n: number) => string) {
  const table = parseLabeledRows(raw);
  const entries: { name: string; value: number }[] = [];
  const skipped = [...table.skipped];
  for (const row of table.rows) {
    const label = parseCell(row.label);
    if (label !== null) {
      // Bare numbers: the label is itself a value, and so is every cell after it.
      for (const value of [label, ...row.values]) {
        if (value !== null) {
          entries.push({ name: fallbackName(entries.length + 1), value });
        }
      }
    } else if (row.values[0] === null || row.values[0] === undefined) {
      skipped.push(row.line);
    } else {
      entries.push({
        name: row.label.trim() || fallbackName(entries.length + 1),
        value: row.values[0],
      });
    }
  }
  return { entries, skipped: skipped.sort((a, b) => a - b) };
}

export default function HerfindahlIndexCalculatorWidget() {
  const strings = useToolUi(HERFINDAHL_INDEX_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [raw, setRaw] = useState<string>(strings.exampleData);
  const [kind, setKind] = useState<Kind>("shares");
  const [rescale, setRescale] = useState(false);
  const [pair, setPair] = useState<[number, number]>([3, 4]);

  const { entries, skipped } = readFirms(raw, (n) =>
    t(strings.firmDefault, { n })
  );
  const negative = entries.some((e) => e.value < 0);
  const positive = entries.filter((e) => e.value > 0);
  const total = positive.reduce((sum, e) => sum + e.value, 0);
  const shares = toShares(
    positive.map((e) => e.value),
    kind,
    rescale
  );
  const firms: Firm[] = positive.map((e, i) => ({
    name: e.name,
    share: shares[i],
  }));
  const off = kind === "shares" && !rescale && Math.abs(total - 100) > 0.01;
  const first = Math.min(pair[0], firms.length - 1);
  const second = Math.min(pair[1], firms.length - 1);
  const merged =
    firms.length >= 2 && first !== second
      ? merger(shares, first, second)
      : null;
  const options = firms.map((f, i) => ({ value: String(i), label: f.name }));

  return (
    <div className="space-y-6">
      <TextAreaField
        hint={strings.dataHint}
        id="hhi-data"
        label={strings.dataLabel}
        onChange={setRaw}
        rows={7}
        value={raw}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField
          id="hhi-kind"
          label={strings.kindLabel}
          onChange={setKind}
          options={[
            { value: "shares", label: strings.kindShares },
            { value: "sales", label: strings.kindSales },
          ]}
          value={kind}
        />
        {kind === "shares" ? (
          <div className="sm:pt-7">
            <Checkbox
              checked={rescale}
              label={strings.rescale}
              onChange={setRescale}
            />
          </div>
        ) : null}
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {negative ? <ErrorNotice>{strings.negative}</ErrorNotice> : null}
        {!negative && firms.length === 0 ? (
          <ErrorNotice>{strings.noFirms}</ErrorNotice>
        ) : null}
        {skipped.length > 0 ? (
          <Notice>
            {tPlural(strings.skippedLines, locale, skipped.length, {
              lines: skipped.map((n) => fmt(n)).join(", "),
            })}
          </Notice>
        ) : null}
        {off && !negative && firms.length > 0 ? (
          <Notice>
            {t(strings.sumNotHundred, {
              total: fmt(total, { maximumFractionDigits: 2 }),
            })}
          </Notice>
        ) : null}
        {!negative && firms.length > 0 ? (
          <ConcentrationView firms={firms} fmt={fmt} strings={strings} />
        ) : null}
      </section>
      {!negative && firms.length >= 2 ? (
        <section aria-live="polite" className="space-y-3">
          <h2 className={WIDGET_H2}>{strings.mergerHeading}</h2>
          <p className="text-muted-foreground text-sm">{strings.mergerHint}</p>
          <div className="grid gap-4 sm:grid-cols-2">
            <SelectField
              id="hhi-first"
              label={strings.firstFirm}
              onChange={(v) => setPair([Number(v), pair[1]])}
              options={options}
              value={String(first)}
            />
            <SelectField
              id="hhi-second"
              label={strings.secondFirm}
              onChange={(v) => setPair([pair[0], Number(v)])}
              options={options}
              value={String(second)}
            />
          </div>
          {merged ? (
            <MergerView
              first={firms[first]}
              fmt={fmt}
              result={merged}
              second={firms[second]}
              strings={strings}
            />
          ) : (
            <ErrorNotice>{strings.sameFirm}</ErrorNotice>
          )}
        </section>
      ) : null}
    </div>
  );
}
